'use client'

import React, { useRef, useCallback } from 'react'
import { useTranslations, useLocale } from 'next-intl'

import topAirlinesData from '@/src/constants/topAirlines.json'
import { getFlightLogo } from '@/src/utils/confirmation/airlineUtils'
import { TOP_AIRLINES } from '@/src/constants/topAirlines'

import { DesktopTopAirlines } from './desktopTopAirlines'

interface TopAirlinesData {
    title: string
    airlines: Array<{
        name_en: string
        name_ar?: string
        href_en: string
        href_ar?: string
    }>
}

interface Airline {
    name: string
    href: string
}

const ADDITIONAL_AIRLINE_MAPPINGS: Record<string, string> = {
    'Air India': 'ai',
    'Qatar Airways': 'qr'
}

const getAirlineCode = (airlineName: string): string => {
    const airline = TOP_AIRLINES.find(a => a.name === airlineName)
    if (airline) return airline.id
    return ADDITIONAL_AIRLINE_MAPPINGS[airlineName] || ''
}

export const DesktopTopAirlinesWrapper: React.FC = () => {
    const t = useTranslations('homePage.headings')
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const isArabic = locale === 'ar'
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const data = topAirlinesData as TopAirlinesData

    if (!data || !data.airlines || data.airlines.length === 0) {
        return null
    }

    const scroll = useCallback(
        (direction: 'left' | 'right') => {
            if (!scrollContainerRef.current) return

            const container = scrollContainerRef.current
            // Desktop: 150px card width + 16px gap (gap: 2 = 16px)
            const cardWidth = 150
            const gap = 16
            const scrollAmount = cardWidth + gap
            const scrollPosition = direction === 'left' ? -scrollAmount : scrollAmount

            container.scrollBy({
                left: isRTL ? -scrollPosition : scrollPosition,
                behavior: 'smooth'
            })
        },
        [isRTL]
    )

    const processedAirlines = data.airlines.map(airline => {
        const displayName = isArabic ? airline.name_ar || airline.name_en : airline.name_en
        const airlineCode = getAirlineCode(airline.name_en)
        const upperCaseCode = airlineCode ? airlineCode.toUpperCase() : ''
        const logoUrl = upperCaseCode
            ? getFlightLogo(upperCaseCode)
            : 'https://ui.cltp.co/images/logos/air-logos/DEFAULT_2x.png'

        return {
            airline: {
                ...airline,
                name: displayName,
                href: isArabic ? airline.href_ar || airline.href_en : airline.href_en
            },
            airlineCode,
            logoUrl
        }
    })

    const handleScrollLeft = useCallback(() => {
        scroll(isRTL ? 'right' : 'left')
    }, [scroll, isRTL])

    const handleScrollRight = useCallback(() => {
        scroll(isRTL ? 'left' : 'right')
    }, [scroll, isRTL])

    return (
        <DesktopTopAirlines
            title={t('topAirlines')}
            airlines={processedAirlines}
            isRTL={isRTL}
            onScrollLeft={handleScrollLeft}
            onScrollRight={handleScrollRight}
            scrollContainerRef={scrollContainerRef}
        />
    )
}

export default DesktopTopAirlinesWrapper
